import { Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Tooltip, Spinner, Button } from "@heroui/react";
import { Info, Paperclip, ArrowClockwise, Clipboard } from "@phosphor-icons/react";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";

interface EmailMessage {
    id: number;
    messageId: string;
    subject: string;
    from: string;
    body: string;
    receivedDateTime: Date;
    isRead: boolean;
    readAt?: Date;
    attachments?: Array<{
        name: string;
        size: number;
        contentType: string;
        sharePointFileId: string;
        sharePointFileUrl: string;
    }>;
}

interface AcquisitionEmailsListProps {
    acquisitionId: number;
}

export const AcquisitionEmailsList = ({ acquisitionId }: AcquisitionEmailsListProps) => {
    const { session } = useSession();
    const [emails, setEmails] = useState<EmailMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchEmails = async () => {
        try {
            setIsLoading(true);
            const response = await fetchWithAuth(
                `${API_URL}/acquisitions/${acquisitionId}/emails`,
                { method: "GET" },
                session
            );
            const data = await response.json();
            setEmails(data);
        } catch (error) {
            console.error('Error fetching emails:', error);
            toast.error('Failed to fetch emails');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSync = async () => {
        try {
            setIsLoading(true);
            await fetchWithAuth(
                `${API_URL}/acquisitions/${acquisitionId}/sync-emails`,
                { method: "POST" },
                session
            );
            await fetchEmails();
            toast.success('Emails synchronized successfully');
        } catch (error) {
            console.error('Error syncing emails:', error);
            toast.error('Failed to sync emails');
        } finally {
            setIsLoading(false);
        }
    };

    const openSharePointFile = (url: string) => {
        window.open(url, '_blank');
    };

    const renderEmailPreview = (email: EmailMessage) => {
        return (
            <div className="flex items-center gap-2">
                <span>{email.subject}</span>
                <Tooltip
                    content={
                        <div className="max-w-[800px] max-h-[80vh] overflow-y-auto">
                            <p className="font-bold mb-2">Van: {email.from}</p>
                            <p className="font-bold mb-2">Ontvangen: {new Date(email.receivedDateTime).toLocaleString()}</p>
                            {email.attachments && email.attachments.length > 0 && (
                                <div className="mb-2">
                                    <p className="font-bold">Bijlagen:</p>
                                    <div className="mt-1 space-y-1">
                                        {email.attachments.map(attachment => (
                                            <div
                                                key={attachment.sharePointFileId}
                                                className="flex items-center gap-2 p-1 hover:bg-gray-100 rounded-sm cursor-pointer"
                                                onClick={() => openSharePointFile(attachment.sharePointFileUrl)}
                                            >
                                                <Paperclip className="w-4 h-4" />
                                                <span>{attachment.name}</span>
                                                <span className="text-sm text-gray-500">
                                                    ({Math.round(attachment.size / 1024)}KB)
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <p className="font-bold">Inhoud:</p>
                            <div
                                className="mt-2"
                                dangerouslySetInnerHTML={{ __html: email.body }}
                            />
                        </div>
                    }
                    placement="left"
                    showArrow
                    classNames={{
                        base: "before:bg-default-200",
                        content: [
                            "py-2 px-4 shadow-xl",
                            "rounded-lg"
                        ],
                    }}
                >
                    <div className="flex items-center gap-2">
                        <Info className="w-4 h-4 cursor-pointer text-neutral-secondary-mid hover:text-neutral-primary" />
                        {email.attachments && email.attachments.length > 0 && (
                            <Paperclip className="w-4 h-4 text-neutral-secondary-mid" />
                        )}
                    </div>
                </Tooltip>
            </div>
        );
    };

    const copyAcquisitionCode = () => {
        const code = `#ACQ-${acquisitionId}`;
        navigator.clipboard.writeText(code)
            .then(() => {
                toast.success(`Code ${code} gekopieerd naar klembord!`, {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            })
            .catch(() => {
                toast.error('Kon code niet kopiëren naar klembord');
            });
    };

    useEffect(() => {
        fetchEmails();
    }, [acquisitionId]);

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold">E-mails</h2>
                    <div
                        className="flex items-center gap-2 cursor-pointer hover:text-neutral-primary text-neutral-secondary-mid transition-colors"
                        onClick={copyAcquisitionCode}
                    >
                        <Tooltip
                            content={`Druk hier om de e-mail code ${`#ACQ-${acquisitionId}`} voor dit dossier te kopiëren. Plak deze daarna in het onderwerp van een nieuwe e-mail naar acquisitions@yally.be om deze hieraan toe te voegen`}
                            placement="left"
                            showArrow
                            classNames={{
                                base: "before:bg-default-200",
                            }}
                        >
                            <Clipboard className="w-4 h-4" />
                        </Tooltip>
                    </div>
                </div>
                <Button
                    onClick={handleSync}
                    isDisabled={isLoading}
                    startContent={<ArrowClockwise className="w-4 h-4" />}
                >
                    Synchroniseer
                </Button>
            </div>

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-8">
                    <Spinner size="lg" color="primary" />
                    <p className="mt-4 text-neutral-secondary-mid">Loading emails...</p>
                </div>
            ) : (
                <Table aria-label="Emails table">
                    <TableHeader>
                        <TableColumn>Subject</TableColumn>
                        <TableColumn>From</TableColumn>
                        <TableColumn>Received</TableColumn>
                    </TableHeader>
                    <TableBody>
                        {emails.map((email) => (
                            <TableRow key={email.id}>
                                <TableCell>{renderEmailPreview(email)}</TableCell>
                                <TableCell>{email.from}</TableCell>
                                <TableCell>{new Date(email.receivedDateTime).toLocaleString()}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
}; 